import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import logger from "../utils/logger";

interface PresignedUrlRequest {
  fileName: string;
  contentType: string;
}

interface PresignedUrlResponse {
  presignedUrl: string;
  fileName: string;
  fileUrl: string;
}

export const uploadApiSlice = createApi({
  reducerPath: "uploadApi",
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_BASE_URL,
    credentials: "include",
  }),
  endpoints: (builder) => ({
    getPresignedUrl: builder.mutation<
      PresignedUrlResponse,
      PresignedUrlRequest
    >({
      query: ({ fileName, contentType }) => ({
        url: `/attachments?fileName=${encodeURIComponent(
          fileName,
        )}&contentType=${encodeURIComponent(contentType)}`,
      }),
    }),
    uploadToS3: builder.mutation<string, { presignedUrl: string; file: File }>({
      async queryFn({ presignedUrl, file }) {
        try {
          const response = await fetch(presignedUrl, {
            method: "PUT",
            body: file,
            headers: {
              "Content-Type": file.type,
            },
            mode: "cors",
            credentials: "omit",
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return { data: presignedUrl };
        } catch (error) {
          logger.error("Failed to upload file", { error, fileName: file.name });
          return { error: { status: 500, data: String(error) } };
        }
      },
    }),
    deleteFile: builder.mutation<void, string>({
      query: (fileUrl) => ({
        url: "/attachments",
        method: "DELETE",
        body: { fileUrl },
      }),
    }),
  }),
});

export const {
  useGetPresignedUrlMutation,
  useUploadToS3Mutation,
  useDeleteFileMutation,
} = uploadApiSlice;

// Usage hook
export const useFileUpload = () => {
  const [getPresignedUrl] = useGetPresignedUrlMutation();
  const [uploadToS3] = useUploadToS3Mutation();

  const uploadFile = async (file: File): Promise<string> => {
    const urlResponse = await getPresignedUrl({
      fileName: file.name,
      contentType: file.type,
    });

    if ("error" in urlResponse) {
      throw new Error("Failed to get upload URL");
    }

    const { presignedUrl, fileUrl } = urlResponse.data;

    const uploadResponse = await uploadToS3({ presignedUrl, file });

    if ("error" in uploadResponse) {
      throw new Error("Failed to upload file");
    }

    return fileUrl;
  };

  return { uploadFile };
};
